import React from 'react';
import { Controller, Control, Path, useWatch } from 'react-hook-form';
import { TextField, Typography, styled, Button } from '@mui/material';
import { Column, Row } from 'further-ui/components/Layout';
import FormField from 'further-ui/components/FormField';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import ProfilePictureUpload from './ProfilePictureUpload';
import { UserFormData } from '../types';

type Props<T extends UserFormData> = {
  control: Control<T>;
  mode: 'add' | 'edit';
  userId?: string;
  currentProfilePicUrl?: string;
};

const StyledColumn = styled(Column)({
  maxWidth: '400px',
});

const UserInformationForm = <T extends UserFormData>({
  control,
  mode,
  userId,
  currentProfilePicUrl,
}: Props<T>): React.ReactElement => {
  const [canManageUserSecurity] = useHasPermissions([
    RolePermission.ManageUserSecurity,
  ]);

  const requestPasswordResetForUser =
    api.adminUser.requestPasswordResetForUser.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Password reset email sent successfully.',
        invalidations: [],
      }),
    );

  const isEditMode = mode === 'edit';

  // Watch firstName and lastName to update avatar display name
  const firstName = useWatch({ control, name: 'firstName' as Path<T> });
  const lastName = useWatch({ control, name: 'lastName' as Path<T> });
  const displayName = `${firstName || ''} ${lastName || ''}`.trim() || '';

  return (
    <StyledColumn spacing="lg" fullWidth>
      {isEditMode && <Typography variant="h5">User information</Typography>}

      <ProfilePictureUpload
        control={control}
        displayName={displayName}
        currentProfilePicUrl={currentProfilePicUrl}
      />

      <Column spacing="md">
        <FormField label="First name">
          <Controller
            name={'firstName' as Path<T>}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                placeholder="First name"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </FormField>

        <FormField label="Last name">
          <Controller
            name={'lastName' as Path<T>}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                placeholder="Last name"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </FormField>

        <FormField label="Email address">
          <Controller
            name={'email' as Path<T>}
            disabled={isEditMode}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                placeholder="Email address"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </FormField>

        {isEditMode && userId && canManageUserSecurity && (
          <FormField label="Password">
            <Column spacing="sm">
              <TextField
                value="*****************"
                disabled
                fullWidth
                size="small"
              />
              <Row justify="flex-start">
                <Button
                  variant="text"
                  onClick={() =>
                    requestPasswordResetForUser.mutate({ id: userId })
                  }
                  disabled={requestPasswordResetForUser.isPending}
                >
                  Send password reset email
                </Button>
              </Row>
            </Column>
          </FormField>
        )}
      </Column>
    </StyledColumn>
  );
};

export default UserInformationForm;
